import {allotmentAvailableBalance, latestReviewedClaim, resolveAllotmentCarryForward} from '../core/allotmentCarryForward'
import React, {FC, useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {KTSVG} from '../../../../_metronic/helpers'
import {showErrorToast} from '../../../shared/utils/apiErrorMessage'
import {Crew, Company, CompanyAdmin, Vessel} from '../core/_models'
import {
  createCrewBankingDetails,
  CrewBankingDetailsRecord,
  getCrewAllotments,
  getCrewBankingDetails,
  getCrewClaims,
  updateCrewBankingDetails,
} from '../core/_requests'
import {
  BankingDetailsForm,
  BankingDetailsFormErrors,
  createEmptyBankingDetailsForm,
  IBAN_MAX_LENGTH,
  IBAN_MIN_LENGTH,
  IFSC_RTGS_LENGTH,
  toBankingDetailsForm,
  toBankingDetailsPayload,
  validateBankingDetailsForm,
} from './bankingDetailsFormUtils'
import {
  blurNumberInputOnWheel,
  preventNumberInputArrowStep,
} from '../../../shared/utils/numberInputGuards'
import { useAuth } from '../../auth'
import SearchableSelect from '../../../shared/components/SearchableSelect'

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'

const resolveBankCode = (
  type?: string | null,
  rtgsCode?: string | null,
  swiftCode?: string | null
): {label: string; value: string} => {
  const normalizedType = (type || '').toString().trim().toUpperCase()
  const rtgs = (rtgsCode || '').toString().trim()
  const swift = (swiftCode || '').toString().trim()

  if (normalizedType === 'RTGS') return {label: 'IFSC / RTGS', value: rtgs || '-'}
  if (normalizedType === 'IFSC') return {label: 'SWIFT / IBAN', value: swift || '-'}
  if (rtgs) return {label: 'IFSC / RTGS', value: rtgs}
  if (swift) return {label: 'SWIFT / IBAN', value: swift}
  return {label: '-', value: '-'}
}

const formatSelectedPeriodLabel = (period?: string) => {
  if (!period) return 'No month selected'

  const match = period.trim().match(/^(\d{4})-(\d{2})$/)
  if (!match) return period

  const year = Number(match[1])
  const month = Number(match[2])
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return period
  }

  return new Date(year, month - 1, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  })
}

const getPreviousPeriod = (period?: string): string | undefined => {
  if (!period) return undefined
  const [yearPart, monthPart] = period.split('-')
  const year = Number(yearPart)
  const month = Number(monthPart)
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return undefined

  const previous = new Date(year, month - 2, 1)
  const previousMonth = String(previous.getMonth() + 1).padStart(2, '0')
  return `${previous.getFullYear()}-${previousMonth}`
}

export type AllotmentAdvanceDraft = {
  crewId: number
  bankingDetails: CrewBankingDetailsRecord | null
  carryForwardAmount: number
  wagesEarned: number
  allotmentAmount: number
  balanceBroughtForward: number
  remarks: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
  onSave: (payload: AllotmentAdvanceDraft, pendingFiles?: File[]) => Promise<void>
  isSaving?: boolean
  selectedPeriod?: string
  crewMembers: Crew[]
  rankMap: Record<number, string>
  companies?: Company[]
  companyAdmins?: CompanyAdmin[]
  vessels?: Vessel[]
  selectedCompanyGroupId?: number | ''
  selectedCompanyAdminId?: number | ''
  selectedVesselId?: number | ''
  onCompanyGroupChange?: (next: number | '') => void
  onCompanyAdminChange?: (next: number | '') => void
  onVesselChange?: (next: number | '') => void
  showCompanyFilter?: boolean
  showSubCompanyFilter?: boolean
  showVesselFilter?: boolean
  lockCrewSelection?: boolean
}

const AddAllotmentAdvanceModal: FC<Props> = ({
  isOpen,
  onClose,
  onSave,
  isSaving = false,
  selectedPeriod,
  crewMembers,
  rankMap,
  companies,
  companyAdmins,
  vessels,
  selectedCompanyGroupId,
  selectedCompanyAdminId,
  selectedVesselId,
  onCompanyGroupChange,
  onCompanyAdminChange,
  onVesselChange,
  showCompanyFilter = true,
  showSubCompanyFilter = true,
  showVesselFilter = true,
  lockCrewSelection = false,
}) => {
  const { hasPermission, hasAnyPermission } = useAuth()
  const canSaveAllotment = hasPermission('CREATE_CREW_ALLOTMENT')
  const canMutateBanking = hasAnyPermission(['CREATE_CREW_ALLOTMENT', 'UPDATE_CREW_ALLOTMENT'])

  const [crewId, setCrewId] = useState<number | ''>('')
  const [bankingAccounts, setBankingAccounts] = useState<CrewBankingDetailsRecord[]>([])
  const [isAddingAccount, setIsAddingAccount] = useState(false)
  const [accountSaveError, setAccountSaveError] = useState('')
  const [isLoadingBank, setIsLoadingBank] = useState(false)
  const [isSavingBank, setIsSavingBank] = useState(false)
  const [editingAccountId, setEditingAccountId] = useState<number | null>(null)
  const [editingAccountForm, setEditingAccountForm] = useState<BankingDetailsForm>(
    createEmptyBankingDetailsForm()
  )
  const [editingAccountErrors, setEditingAccountErrors] = useState<BankingDetailsFormErrors>({})
  const [editingAccountSaveError, setEditingAccountSaveError] = useState('')
  const [isUpdatingAccount, setIsUpdatingAccount] = useState(false)

  const [newAccountForm, setNewAccountForm] = useState<BankingDetailsForm>(
    createEmptyBankingDetailsForm()
  )
  const [newAccountErrors, setNewAccountErrors] = useState<BankingDetailsFormErrors>({})

  const [currentMonthNetWages, setCurrentMonthNetWages] = useState(0)
  const [previousBalanceCf, setPreviousBalanceCf] = useState<number | ''>(0)
  const [hasPreviousPeriodRecord, setHasPreviousPeriodRecord] = useState(false)
  const [accountAllotments, setAccountAllotments] = useState<Record<string, string>>({})
  const [hasReviewedClaim, setHasReviewedClaim] = useState(false)
  const [claimBfAmount, setClaimBfAmount] = useState(0)

  const [wagesEarned, setWagesEarned] = useState(0)

  const canAutoSelectSingleCrew = lockCrewSelection && crewMembers.length === 1

  useEffect(() => {
    if (!isOpen) return
    setCrewId('')
    setBankingAccounts([])
    setIsAddingAccount(false)
    setAccountSaveError('')
    setEditingAccountId(null)
    setEditingAccountForm(createEmptyBankingDetailsForm())
    setEditingAccountErrors({})
    setEditingAccountSaveError('')
    setNewAccountForm(createEmptyBankingDetailsForm())
    setNewAccountErrors({})
    setCurrentMonthNetWages(0)
    setPreviousBalanceCf(0)
    setHasReviewedClaim(false)
    setClaimBfAmount(0)
    setHasPreviousPeriodRecord(false)
    setWagesEarned(0)
    setAccountAllotments({})
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !lockCrewSelection) return

    if (crewMembers.length === 1) {
      const onlyCrewId = Number(crewMembers[0]?.id)
      if (Number.isFinite(onlyCrewId) && onlyCrewId > 0 && crewId !== onlyCrewId) {
        setCrewId(onlyCrewId)
      }
      return
    }

    if (crewMembers.length === 0 && crewId !== '') {
      setCrewId('')
    }
  }, [isOpen, lockCrewSelection, crewMembers, crewId])

  const filteredCompanyAdmins = useMemo(() => {
    if (!companies) return []
    if (!selectedCompanyGroupId) return companies
    return companies.filter((ca) => ca.cga?.id === Number(selectedCompanyGroupId))
  }, [companies, selectedCompanyGroupId])

  const shouldShowSubCompanyFilterField = useMemo(() => {
    if (!showSubCompanyFilter) return false
    if (!selectedCompanyGroupId) return true
    return filteredCompanyAdmins.length > 0
  }, [showSubCompanyFilter, selectedCompanyGroupId, filteredCompanyAdmins])

  const filteredVessels = useMemo(() => {
    if (!vessels) return []
    const getVesselCompanyAdminId = (v: Vessel): number | undefined =>
      v.companyAdmin?.id ?? (v as any).companyAdminId ?? undefined

    const getVesselCompanyGroupId = (v: Vessel): number | undefined =>
      (v as any).companyGroupAdminId ??
      (v as any).companyGroupAdmin?.id ??
      (v.companyAdmin as any)?.cgaid?.id ??
      (v.companyAdmin as any)?.cga?.id

    if (selectedCompanyAdminId) {
      return vessels.filter((v) => getVesselCompanyAdminId(v) === Number(selectedCompanyAdminId))
    }
    if (selectedCompanyGroupId) {
      return vessels.filter((v) => getVesselCompanyGroupId(v) === Number(selectedCompanyGroupId))
    }
    return vessels
  }, [vessels, selectedCompanyAdminId, selectedCompanyGroupId])

  useEffect(() => {
    if (!crewId) {
      setBankingAccounts([])
      setIsAddingAccount(false)
      setAccountSaveError('')
      setEditingAccountId(null)
      setEditingAccountForm(createEmptyBankingDetailsForm())
      setEditingAccountErrors({})
      setEditingAccountSaveError('')
      setCurrentMonthNetWages(0)
      setPreviousBalanceCf(0)
      setHasPreviousPeriodRecord(false)
      setWagesEarned(0)
      setAccountAllotments({})
      return
    }

    let isMounted = true
    const load = async () => {
      setIsLoadingBank(true)
      try {
        const allotmentPeriods = selectedPeriod
          ? [selectedPeriod, getPreviousPeriod(selectedPeriod)].filter(
              (period): period is string => Boolean(period)
            )
          : [undefined]
        const [details, claims, periodAllotments] = await Promise.all([
          getCrewBankingDetails(Number(crewId)),
          getCrewClaims().catch(() => []),
          Promise.all(
            allotmentPeriods.map((period) =>
              getCrewAllotments(Number(crewId), period).catch(() => [])
            )
          ),
        ])
        const allotments = periodAllotments.flat()

        const monthScopedClaims = selectedPeriod
          ? claims.filter((claim) => (claim.claimMonth || '').startsWith(selectedPeriod))
          : claims

        const latestClaimForCrew = [...monthScopedClaims]
          .filter((claim) => Number(claim.crewId) === Number(crewId))
          .sort((a, b) => {
            const aTime = new Date(a.claimMonth || '').getTime() || 0
            const bTime = new Date(b.claimMonth || '').getTime() || 0
            if (bTime !== aTime) return bTime - aTime
            return (Number(b.id) || 0) - (Number(a.id) || 0)
          })[0]

        const isOfficeReviewed =
          latestClaimForCrew?.status === 'REVIEWED' || Boolean(latestClaimForCrew?.reviewedAt)
        const isRejected = latestClaimForCrew?.status === 'REJECTED'
        const hasReviewedClaimRecord = Boolean(latestClaimForCrew && isOfficeReviewed)
        const parsedClaimBfAmount = hasReviewedClaimRecord ? Number((latestClaimForCrew as any)?.bfAmount || 0) : 0
        
        let computedBalanceWages = 0
        if (latestClaimForCrew && isOfficeReviewed) {
          if (isRejected) {
            const bfAmount = Number((latestClaimForCrew as any).bfAmount || 0)
            const wagesEarned = Number((latestClaimForCrew as any).wagesEarned || 0)
            const deductionsTotal = Number((latestClaimForCrew as any).deductionsTotal || 0)
            computedBalanceWages = Math.max(0, bfAmount + wagesEarned - deductionsTotal)
          } else {
            const rawBalanceCf = (latestClaimForCrew as any).balanceCf
            const totalClaim = Number((latestClaimForCrew as any).totalClaim || 0)
            computedBalanceWages =
              rawBalanceCf == null || Number.isNaN(Number(rawBalanceCf))
                ? totalClaim
                : Number(rawBalanceCf)
          }
        }
        const computedWagesEarned = Number((latestClaimForCrew as any)?.wagesEarned || 0)

        const currentPeriodAllotments = selectedPeriod
          ? allotments.filter((a) => (a.statementMonth || '').startsWith(selectedPeriod))
          : []
        const currentPeriodAllotment = currentPeriodAllotments.length > 0
          ? [...currentPeriodAllotments].sort((a, b) => (Number(b.allotmentId || (b as any).id) || 0) - (Number(a.allotmentId || (a as any).id) || 0))[0]
          : null

        let autoPrevBalance = 0
        let hasPrevRecord = false
        let initialAllotmentAmount = 0

        if (currentPeriodAllotment) {
          if (currentPeriodAllotment.balanceBroughtForward != null) {
            autoPrevBalance = Number(currentPeriodAllotment.balanceBroughtForward) || 0
          }
          initialAllotmentAmount = Number(currentPeriodAllotment.allotmentAmount ?? currentPeriodAllotment.amount ?? 0) || 0

          const prevPeriodKey = getPreviousPeriod(selectedPeriod)
          if (prevPeriodKey) {
            const hasPrior = allotments.some((a) => (a.statementMonth || '').startsWith(prevPeriodKey)) ||
                             claims.some((c) => Number(c.crewId) === Number(crewId) && (c.claimMonth || '').startsWith(prevPeriodKey))
            hasPrevRecord = hasPrior
            const priorAllotment = allotments
              .filter((a) => (a.statementMonth || '').startsWith(prevPeriodKey))
              .sort((a, b) => Number(b.allotmentId || 0) - Number(a.allotmentId || 0))[0]
            if (priorAllotment) {
              autoPrevBalance = resolveAllotmentCarryForward(priorAllotment,
                latestReviewedClaim(claims, crewId, prevPeriodKey))
            }
          }
        } else {
          const prevPeriodKey = getPreviousPeriod(selectedPeriod)
          if (prevPeriodKey) {
            const prevAllotments = allotments
              .filter((a) => (a.statementMonth || '').startsWith(prevPeriodKey))
              .sort((a, b) => (Number(b.allotmentId || (b as any).id) || 0) - (Number(a.allotmentId || (a as any).id) || 0))

            const prevClaims = claims
              .filter((c) => Number(c.crewId) === Number(crewId) && (c.claimMonth || '').startsWith(prevPeriodKey))
              .sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0))

            if (prevAllotments.length > 0 || prevClaims.length > 0) {
              hasPrevRecord = true
              const latestPrevAllotment = prevAllotments[0]
              const reviewedPrevClaim = latestReviewedClaim(prevClaims, crewId, prevPeriodKey)
              if (latestPrevAllotment) {
                autoPrevBalance = resolveAllotmentCarryForward(latestPrevAllotment, reviewedPrevClaim)
              } else if (reviewedPrevClaim && (reviewedPrevClaim as any).balanceCf != null) {
                autoPrevBalance = Number((reviewedPrevClaim as any).balanceCf) || 0
              }
            }
          }
        }

        const netWages = Number.isFinite(computedBalanceWages) ? computedBalanceWages : 0

        if (isMounted) {
          setBankingAccounts(details)
          setIsAddingAccount(false)
          setAccountSaveError('')
          setEditingAccountId(null)
          setEditingAccountForm(createEmptyBankingDetailsForm())
          setEditingAccountErrors({})
          setEditingAccountSaveError('')
          setCurrentMonthNetWages(netWages)
          setPreviousBalanceCf(autoPrevBalance)
          setHasPreviousPeriodRecord(hasPrevRecord)
          setWagesEarned(Number.isFinite(computedWagesEarned) ? computedWagesEarned : 0)
          setHasReviewedClaim(hasReviewedClaimRecord)
          setClaimBfAmount(Number.isFinite(parsedClaimBfAmount) ? parsedClaimBfAmount : 0)
          setAccountAllotments(() => {
            const next: Record<string, string> = {}
            details.forEach((account, index) => {
              const key = String(account.id ?? `acc-${index}`)
              next[key] = index === 0 && initialAllotmentAmount > 0 ? String(initialAllotmentAmount) : ''
            })
            return next
          })
        }
      } finally {
        if (isMounted) setIsLoadingBank(false)
      }
    }

    load()
    return () => {
      isMounted = false
    }
  }, [crewId, selectedPeriod])

  const canAddAnotherAccount = Boolean(crewId) && bankingAccounts.length < 3

  const toAmount = (value: string | number | null | undefined) => {
    const parsed = Number(value ?? 0)
    return Number.isFinite(parsed) ? parsed : 0
  }

  const getAccountKey = (account: CrewBankingDetailsRecord, index: number) =>
    String(account.id ?? `acc-${index}`)

  const totalAssignedAmount = bankingAccounts.reduce((sum, account, index) => {
    const key = getAccountKey(account, index)
    return sum + toAmount(accountAllotments[key])
  }, 0)

  const effectivePreviousBalanceCf = typeof previousBalanceCf === 'number' ? previousBalanceCf : 0
  const balanceWages = allotmentAvailableBalance(currentMonthNetWages,
    effectivePreviousBalanceCf, hasReviewedClaim, claimBfAmount)

  const remainingBalanceCf = Math.max(balanceWages - totalAssignedAmount, 0)
  const isExceedingBalance = totalAssignedAmount > balanceWages

  const handleAccountAllotmentChange = (key: string, value: string) => {
    const sanitized = value === '' ? '' : String(value)
    setAccountAllotments((prev) => ({...prev, [key]: sanitized}))
  }

  const handlePreviousBalanceCfChange = (value: string) => {
    if (hasPreviousPeriodRecord) return
    if (value === '') {
      setPreviousBalanceCf('')
    } else {
      const num = Number(value)
      if (Number.isFinite(num)) {
        setPreviousBalanceCf(num)
      }
    }
  }

  const handleNewAccountChange = (key: keyof BankingDetailsForm, value: string) => {
    setNewAccountForm((prev) => {
      const next = {...prev, [key]: value} as BankingDetailsForm
      if (key === 'accountCurrency' && value !== 'OTHER') {
        next.otherCurrency = ''
      }
      return next
    })

    setNewAccountErrors((prev) => {
      if (!prev[key] && !(key === 'accountCurrency' && prev.otherCurrency)) {
        return prev
      }
      const next = {...prev}
      delete next[key]
      if (key === 'accountCurrency' && value !== 'OTHER') {
        delete next.otherCurrency
      }
      return next
    })
  }

  const handleEditingAccountChange = (key: keyof BankingDetailsForm, value: string) => {
    setEditingAccountForm((prev) => {
      const next = {...prev, [key]: value} as BankingDetailsForm
      if (key === 'accountCurrency' && value !== 'OTHER') {
        next.otherCurrency = ''
      }
      return next
    })

    setEditingAccountErrors((prev) => {
      if (!prev[key] && !(key === 'accountCurrency' && prev.otherCurrency)) {
        return prev
      }
      const next = {...prev}
      delete next[key]
      if (key === 'accountCurrency' && value !== 'OTHER') {
        delete next.otherCurrency
      }
      return next
    })
  }

  const beginEditAccount = (account: CrewBankingDetailsRecord, index: number) => {
    if (!account.id || index === 0) return
    setEditingAccountId(account.id)
    setEditingAccountForm(toBankingDetailsForm(account))
    setEditingAccountErrors({})
    setEditingAccountSaveError('')
    setIsAddingAccount(false)
    setAccountSaveError('')
  }

  const cancelEditAccount = () => {
    setEditingAccountId(null)
    setEditingAccountForm(createEmptyBankingDetailsForm())
    setEditingAccountErrors({})
    setEditingAccountSaveError('')
  }

  const handleSaveNewAccount = async () => {
    if (!crewId || !canAddAnotherAccount || !canMutateBanking) return

    const validationErrors = validateBankingDetailsForm(newAccountForm)
    if (Object.keys(validationErrors).length > 0) {
      setNewAccountErrors(validationErrors)
      setAccountSaveError('')
      return
    }

    setIsSavingBank(true)
    setAccountSaveError('')
    setNewAccountErrors({})
    try {
      const saved = await createCrewBankingDetails(
        Number(crewId),
        toBankingDetailsPayload(newAccountForm)
      )

      setBankingAccounts((prev) => {
        return [...prev, saved].slice(0, 3)
      })
      setIsAddingAccount(false)
      setNewAccountForm(createEmptyBankingDetailsForm())
      setNewAccountErrors({})
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Failed to save bank account.'
      setAccountSaveError(message)
      showErrorToast(error, 'Failed to save bank account.', 'save')
    } finally {
      setIsSavingBank(false)
    }
  }

  const handleUpdateAccount = async () => {
    if (!crewId || !editingAccountId || !canMutateBanking) return

    const validationErrors = validateBankingDetailsForm(editingAccountForm)
    if (Object.keys(validationErrors).length > 0) {
      setEditingAccountErrors(validationErrors)
      setEditingAccountSaveError('')
      return
    }

    setIsUpdatingAccount(true)
    setEditingAccountSaveError('')
    setEditingAccountErrors({})
    try {
      const updated = await updateCrewBankingDetails(
        Number(crewId),
        editingAccountId,
        toBankingDetailsPayload(editingAccountForm)
      )
      setBankingAccounts((prev) =>
        prev.map((account) => (Number(account.id) === Number(updated.id) ? updated : account))
      )
      cancelEditAccount()
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Failed to update bank account.'
      setEditingAccountSaveError(message)
      showErrorToast(error, 'Failed to update bank account.', 'update')
    } finally {
      setIsUpdatingAccount(false)
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!crewId) {
      toast.error('Crew member is required')
      return
    }
    if (!canSaveAllotment) {
      toast.error('You do not have permission to save allotments')
      return
    }

    if (isExceedingBalance) {
      const message = 'Total allotment amount cannot exceed Balance Wages.'
      setAccountSaveError(message)
      toast.error(message)
      return
    }

    const accountWithAmount = bankingAccounts.find((account, index) => {
      const key = getAccountKey(account, index)
      return toAmount(accountAllotments[key]) > 0
    })
    const primaryBankingAccount = accountWithAmount || bankingAccounts[0] || null

    const allocationSummary = bankingAccounts
      .map((account, index) => {
        const key = getAccountKey(account, index)
        const amount = toAmount(accountAllotments[key])
        if (amount <= 0) return ''
        return `Acc ${index + 1}: ${amount}`
      })
      .filter(Boolean)
      .join(', ')

    const payload: AllotmentAdvanceDraft = {
      crewId: Number(crewId),
      bankingDetails: primaryBankingAccount,
      carryForwardAmount: Number(remainingBalanceCf || 0),
      wagesEarned: Number(wagesEarned || 0),
      allotmentAmount: Number(totalAssignedAmount || 0),
      balanceBroughtForward: Number(effectivePreviousBalanceCf || 0),
      remarks: allocationSummary ? `Allocations - ${allocationSummary}` : '',
    }

    await onSave(payload)
  }

  if (!isOpen) return null

  return (
    <div className='modal-overlay' onClick={onClose}>
      <div
        className='modal-content'
        onClick={(e) => e.stopPropagation()}
        style={{maxWidth: '1100px', maxHeight: '850px'}}
      >
        <div className='custom-modal-header d-flex justify-content-between align-items-center'>
          <h5 className='m-0'>Add Allotment and Advance</h5>
          <button className='close-btn' onClick={onClose}>
            <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
          </button>
        </div>

        <div className='custom-modal-body' style={{maxHeight: '70vh', overflowY: 'auto'}}>
          <form onSubmit={handleSubmit}>
            <div className='row g-4'>
              {showCompanyFilter && (
                <div className='col-md-4'>
                  <label className={LABEL}>Company</label>
                  <SearchableSelect
                    className='form-select'
                    value={selectedCompanyGroupId ?? ''}
                    onChange={(event) =>
                      onCompanyGroupChange?.(event.target.value ? Number(event.target.value) : '')
                    }
                  >
                    <option value=''>All Companies</option>
                    {(companyAdmins || []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              )}

              {shouldShowSubCompanyFilterField && (
                <div className='col-md-4'>
                  <label className={LABEL}>Sub-Company</label>
                  <SearchableSelect
                    className='form-select'
                    value={selectedCompanyAdminId ?? ''}
                    onChange={(event) =>
                      onCompanyAdminChange?.(event.target.value ? Number(event.target.value) : '')
                    }
                  >
                    <option value=''>All Sub-Companies</option>
                    {filteredCompanyAdmins.map((ca) => (
                      <option key={ca.id} value={ca.id}>
                        {ca.name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              )}

              {showVesselFilter && (
                <div className='col-md-4'>
                  <label className={LABEL}>Vessel</label>
                  <SearchableSelect
                    className='form-select'
                    value={selectedVesselId ?? ''}
                    onChange={(event) =>
                      onVesselChange?.(event.target.value ? Number(event.target.value) : '')
                    }
                  >
                    <option value=''>All Vessels</option>
                    {filteredVessels.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.fleet_name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              )}

              {!canAutoSelectSingleCrew && (
                <div className='col-md-6'>
                  <label className={LABEL}>
                    Crew Member <span className='text-danger'>*</span>
                  </label>
                  <SearchableSelect
                    className='form-select'
                    value={crewId}
                    onChange={(event) =>
                      setCrewId(event.target.value ? Number(event.target.value) : '')
                    }
                    required
                  >
                    <option value=''>Select Crew Member</option>
                    {crewMembers.map((crew) => (
                      <option key={crew.id} value={crew.id}>
                        {crew.name} {rankMap[crew.rankId] ? `(${rankMap[crew.rankId]})` : ''}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              )}

              <div className={canAutoSelectSingleCrew ? 'col-md-4' : 'col-md-2'}>
                <label className={LABEL}>Statement Month</label>
                <input
                  type='text'
                  className='form-control form-control-solid fw-semibold'
                  value={formatSelectedPeriodLabel(selectedPeriod)}
                  readOnly
                />
              </div>

              <div className={canAutoSelectSingleCrew ? 'col-md-4' : 'col-md-2'}>
                <label className={LABEL}>Balance Wages</label>
                <input
                  type='number'
                  className='form-control form-control-solid fw-bold'
                  value={Number(balanceWages || 0)}
                  readOnly
                />
              </div>

              <div className={canAutoSelectSingleCrew ? 'col-md-4' : 'col-md-2'}>
                <label className={LABEL}>Wages Earned</label>
                <input
                  type='number'
                  className='form-control form-control-solid fw-bold'
                  value={Number(wagesEarned || 0)}
                  readOnly
                />
              </div>

              <div className='col-12'>
                <div className='d-flex flex-wrap justify-content-between align-items-center gap-3'>
                  <div className='fw-semibold text-dark'>Banking Details</div>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary'
                    onClick={() => {
                      setIsAddingAccount((prev) => !prev)
                      setAccountSaveError('')
                      cancelEditAccount()
                      setNewAccountForm(createEmptyBankingDetailsForm())
                      setNewAccountErrors({})
                    }}
                    disabled={
                      !canAddAnotherAccount ||
                      isSavingBank ||
                      isLoadingBank ||
                      !canMutateBanking
                    }
                  >
                    Add
                  </button>
                </div>
                {isLoadingBank && <div className='text-muted fs-8'>Loading banking details...</div>}
                {!isLoadingBank && bankingAccounts.length === 0 && crewId && (
                  <div className='text-muted fs-8'>
                    No banking account found. Add Acc 1 to continue.
                  </div>
                )}
                {!isLoadingBank && isExceedingBalance && (
                  <div className='text-danger fs-8'>
                    Total allotment amount cannot exceed Balance Wages.
                  </div>
                )}
              </div>

              {!isLoadingBank &&
                bankingAccounts.map((account, index) => {
                  const detailsBankCode = resolveBankCode(
                    account.ifscRtgsType,
                    account.ifscRtgsCode,
                    account.swiftIandi
                  )
                  const normalizedAccountCurrency = (
                    account.accountCurrency ||
                    account.salaryCurrency ||
                    ''
                  )
                    .toString()
                    .trim()
                    .toUpperCase()
                  const accountKey = getAccountKey(account, index)
                  const isEditingCurrentAccount =
                    account.id != null && Number(account.id) === Number(editingAccountId)
                  const currentBankCodeLabel = isEditingCurrentAccount
                    ? editingAccountForm.bankCodeType === 'IFSC_RTGS'
                      ? 'IFSC / RTGS'
                      : 'SWIFT / IBAN'
                    : detailsBankCode.label
                  const currentBankCodeValue = isEditingCurrentAccount
                    ? editingAccountForm.bankCodeValue
                    : detailsBankCode.value === '-'
                    ? ''
                    : detailsBankCode.value
                  const currentAccountCurrency = isEditingCurrentAccount
                    ? editingAccountForm.accountCurrency
                    : (account.accountCurrency || account.salaryCurrency || '').toString()
                  const showOtherCurrencyField =
                    (isEditingCurrentAccount && editingAccountForm.accountCurrency === 'OTHER') ||
                    (!isEditingCurrentAccount && normalizedAccountCurrency === 'OTHER')
                  const canEditSavedAccount = index > 0

                  return (
                    <div key={account.id || index} className='col-12'>
                      <div
                        className='border rounded p-3'
                        style={{
                          borderColor: '#e4e6ef',
                          backgroundColor: '#fff',
                        }}
                      >
                        <div className='d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3'>
                          <div className='fw-bold text-dark'>Acc {index + 1}</div>
                          <div className='d-flex align-items-center gap-2'>
                            {isEditingCurrentAccount ? (
                              <>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-primary'
                                  onClick={handleUpdateAccount}
                                  disabled={isUpdatingAccount || !canMutateBanking}
                                >
                                  {isUpdatingAccount ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light'
                                  onClick={cancelEditAccount}
                                  disabled={isUpdatingAccount}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : canEditSavedAccount ? (
                              <button
                                type='button'
                                className='btn btn-sm btn-light-primary'
                                onClick={() => beginEditAccount(account, index)}
                                disabled={
                                  isSavingBank ||
                                  isLoadingBank ||
                                  isUpdatingAccount ||
                                  !canMutateBanking
                                }
                              >
                                Edit
                              </button>
                            ) : null}
                          </div>
                        </div>
                        {isEditingCurrentAccount && editingAccountSaveError ? (
                          <div className='text-danger fs-8 mb-3'>{editingAccountSaveError}</div>
                        ) : null}

                        <div className='row g-3'>
                          <div className='col-md-3'>
                            <label className={LABEL}>Beneficiary</label>
                            <input
                              type='text'
                              className={`form-control ${
                                isEditingCurrentAccount ? '' : 'form-control-solid'
                              } ${
                                editingAccountErrors.nameOfBeneficiary && isEditingCurrentAccount
                                  ? 'is-invalid'
                                  : ''
                              }`}
                              value={
                                isEditingCurrentAccount
                                  ? editingAccountForm.nameOfBeneficiary
                                  : account.nameOfBeneficiary || ''
                              }
                              placeholder='—'
                              onChange={(event) =>
                                handleEditingAccountChange('nameOfBeneficiary', event.target.value)
                              }
                              readOnly={!isEditingCurrentAccount}
                            />
                            {isEditingCurrentAccount && editingAccountErrors.nameOfBeneficiary ? (
                              <div className='invalid-feedback'>
                                {editingAccountErrors.nameOfBeneficiary}
                              </div>
                            ) : null}
                          </div>
                          <div className='col-md-3'>
                            <label className={LABEL}>Bank</label>
                            <input
                              type='text'
                              className={`form-control ${
                                isEditingCurrentAccount ? '' : 'form-control-solid'
                              } ${
                                editingAccountErrors.bankName && isEditingCurrentAccount
                                  ? 'is-invalid'
                                  : ''
                              }`}
                              value={
                                isEditingCurrentAccount
                                  ? editingAccountForm.bankName
                                  : account.bankName || ''
                              }
                              placeholder='—'
                              onChange={(event) =>
                                handleEditingAccountChange('bankName', event.target.value)
                              }
                              readOnly={!isEditingCurrentAccount}
                            />
                            {isEditingCurrentAccount && editingAccountErrors.bankName ? (
                              <div className='invalid-feedback'>
                                {editingAccountErrors.bankName}
                              </div>
                            ) : null}
                          </div>
                          <div className='col-md-3'>
                            <label className={LABEL}>Account Number</label>
                            <input
                              type='text'
                              className={`form-control ${
                                isEditingCurrentAccount ? '' : 'form-control-solid'
                              } ${
                                editingAccountErrors.accountNumber && isEditingCurrentAccount
                                  ? 'is-invalid'
                                  : ''
                              }`}
                              value={
                                isEditingCurrentAccount
                                  ? editingAccountForm.accountNumber
                                  : account.accountNumber || ''
                              }
                              placeholder='—'
                              onChange={(event) =>
                                handleEditingAccountChange('accountNumber', event.target.value)
                              }
                              readOnly={!isEditingCurrentAccount}
                            />
                            {isEditingCurrentAccount && editingAccountErrors.accountNumber ? (
                              <div className='invalid-feedback'>
                                {editingAccountErrors.accountNumber}
                              </div>
                            ) : null}
                          </div>
                          <div className='col-md-3'>
                            <label className={LABEL}>Branch Name</label>
                            <input
                              type='text'
                              className={`form-control ${
                                isEditingCurrentAccount ? '' : 'form-control-solid'
                              } ${
                                editingAccountErrors.branchName && isEditingCurrentAccount
                                  ? 'is-invalid'
                                  : ''
                              }`}
                              value={
                                isEditingCurrentAccount
                                  ? editingAccountForm.branchName
                                  : account.branchName || ''
                              }
                              placeholder='—'
                              onChange={(event) =>
                                handleEditingAccountChange('branchName', event.target.value)
                              }
                              readOnly={!isEditingCurrentAccount}
                            />
                            {isEditingCurrentAccount && editingAccountErrors.branchName ? (
                              <div className='invalid-feedback'>
                                {editingAccountErrors.branchName}
                              </div>
                            ) : null}
                          </div>
                          <div className='col-md-3'>
                            <label className={LABEL}>Branch Code</label>
                            <input
                              type='text'
                              className={`form-control ${
                                isEditingCurrentAccount ? '' : 'form-control-solid'
                              } ${
                                editingAccountErrors.branchCode && isEditingCurrentAccount
                                  ? 'is-invalid'
                                  : ''
                              }`}
                              value={
                                isEditingCurrentAccount
                                  ? editingAccountForm.branchCode
                                  : account.branchCode || ''
                              }
                              placeholder='—'
                              onChange={(event) =>
                                handleEditingAccountChange('branchCode', event.target.value)
                              }
                              readOnly={!isEditingCurrentAccount}
                            />
                            {isEditingCurrentAccount && editingAccountErrors.branchCode ? (
                              <div className='invalid-feedback'>
                                {editingAccountErrors.branchCode}
                              </div>
                            ) : null}
                          </div>
                          <div className='col-md-3'>
                            <label className={LABEL}>Bank Code</label>
                            {isEditingCurrentAccount ? (
                              <SearchableSelect
                                className='form-select'
                                value={editingAccountForm.bankCodeType}
                                onChange={(event) =>
                                  handleEditingAccountChange(
                                    'bankCodeType',
                                    event.target.value as BankingDetailsForm['bankCodeType']
                                  )
                                }
                              >
                                <option value='IFSC_RTGS'>IFSC / RTGS</option>
                                <option value='SWIFT_IBAN'>SWIFT / IBAN</option>
                              </SearchableSelect>
                            ) : (
                              <input
                                type='text'
                                className='form-control form-control-solid'
                                value={currentBankCodeLabel}
                                placeholder='—'
                                readOnly
                              />
                            )}
                          </div>
                          <div className='col-md-3'>
                            <label className={LABEL}>Bank Code Value</label>
                            <input
                              type='text'
                              className={`form-control ${
                                isEditingCurrentAccount ? '' : 'form-control-solid'
                              } ${
                                editingAccountErrors.bankCodeValue && isEditingCurrentAccount
                                  ? 'is-invalid'
                                  : ''
                              }`}
                              value={currentBankCodeValue}
                              placeholder='—'
                              onChange={(event) =>
                                handleEditingAccountChange(
                                  'bankCodeValue',
                                  event.target.value.replace(/\s+/g, '').toUpperCase()
                                )
                              }
                              readOnly={!isEditingCurrentAccount}
                              maxLength={
                                isEditingCurrentAccount
                                  ? editingAccountForm.bankCodeType === 'IFSC_RTGS'
                                    ? IFSC_RTGS_LENGTH
                                    : IBAN_MAX_LENGTH
                                  : undefined
                              }
                            />
                            {isEditingCurrentAccount && editingAccountErrors.bankCodeValue ? (
                              <div className='invalid-feedback'>
                                {editingAccountErrors.bankCodeValue}
                              </div>
                            ) : null}
                          </div>
                          <div className='col-md-3'>
                            <label className={LABEL}>Bank Address</label>
                            <input
                              type='text'
                              className={`form-control ${
                                isEditingCurrentAccount ? '' : 'form-control-solid'
                              } ${
                                editingAccountErrors.bankAddress && isEditingCurrentAccount
                                  ? 'is-invalid'
                                  : ''
                              }`}
                              value={
                                isEditingCurrentAccount
                                  ? editingAccountForm.bankAddress
                                  : account.bankAddress || ''
                              }
                              placeholder='—'
                              onChange={(event) =>
                                handleEditingAccountChange('bankAddress', event.target.value)
                              }
                              readOnly={!isEditingCurrentAccount}
                            />
                            {isEditingCurrentAccount && editingAccountErrors.bankAddress ? (
                              <div className='invalid-feedback'>
                                {editingAccountErrors.bankAddress}
                              </div>
                            ) : null}
                          </div>
                          <div className='col-md-3'>
                            <label className={LABEL}>Account Currency</label>
                            {isEditingCurrentAccount ? (
                              <SearchableSelect
                                className={`form-select ${
                                  editingAccountErrors.accountCurrency ? 'is-invalid' : ''
                                }`}
                                value={editingAccountForm.accountCurrency}
                                onChange={(event) =>
                                  handleEditingAccountChange(
                                    'accountCurrency',
                                    event.target.value as BankingDetailsForm['accountCurrency']
                                  )
                                }
                              >
                                <option value='USD'>USD</option>
                                <option value='EUR'>EUR</option>
                                <option value='GBP'>GBP</option>
                                <option value='SGD'>SGD</option>
                                <option value='INR'>INR</option>
                                <option value='AED'>AED</option>
                                <option value='OTHER'>OTHER</option>
                              </SearchableSelect>
                            ) : (
                              <input
                                type='text'
                                className='form-control form-control-solid'
                                value={currentAccountCurrency}
                                placeholder='—'
                                readOnly
                              />
                            )}
                            {isEditingCurrentAccount && editingAccountErrors.accountCurrency ? (
                              <div className='invalid-feedback'>
                                {editingAccountErrors.accountCurrency}
                              </div>
                            ) : null}
                          </div>
                          {showOtherCurrencyField ? (
                            <div className='col-md-3'>
                              <label className={LABEL}>Account Other Currency</label>
                              <input
                                type='text'
                                className={`form-control ${
                                  isEditingCurrentAccount ? '' : 'form-control-solid'
                                } ${
                                  editingAccountErrors.otherCurrency && isEditingCurrentAccount
                                    ? 'is-invalid'
                                    : ''
                                }`}
                                value={
                                  isEditingCurrentAccount
                                    ? editingAccountForm.otherCurrency
                                    : account.otherCurrency || account.salaryOtherCurrency || ''
                                }
                                placeholder='—'
                                onChange={(event) =>
                                  handleEditingAccountChange(
                                    'otherCurrency',
                                    event.target.value.toUpperCase()
                                  )
                                }
                                readOnly={!isEditingCurrentAccount}
                                maxLength={3}
                              />
                              {isEditingCurrentAccount && editingAccountErrors.otherCurrency ? (
                                <div className='invalid-feedback'>
                                  {editingAccountErrors.otherCurrency}
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          <div className='col-md-3'>
                            <label className={LABEL}>Remarks</label>
                            <input
                              type='text'
                              className={`form-control ${
                                isEditingCurrentAccount ? '' : 'form-control-solid'
                              }`}
                              value={
                                isEditingCurrentAccount
                                  ? editingAccountForm.remarks
                                  : account.remarks || ''
                              }
                              placeholder='—'
                              onChange={(event) =>
                                handleEditingAccountChange('remarks', event.target.value)
                              }
                              readOnly={!isEditingCurrentAccount}
                            />
                          </div>
                          <div className='col-md-3'>
                            <label className={LABEL}>Allotment Amount</label>
                            <input
                              type='number'
                              min={0}
                              step='0.01'
                              className='form-control'
                              placeholder='0.00'
                              value={accountAllotments[accountKey] ?? ''}
                              onChange={(event) =>
                                handleAccountAllotmentChange(accountKey, event.target.value)
                              }
                              onWheel={blurNumberInputOnWheel}
                              onKeyDown={preventNumberInputArrowStep}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}

              {!isLoadingBank && bankingAccounts.length > 0 && (
                <div className='col-12'>
                  <div className='border rounded p-3 bg-light'>
                    <div className='row g-3'>
                      {!hasPreviousPeriodRecord && (
                        <div className='col-md-3'>
                          <label className={LABEL}>Previous Balance C/F</label>
                          <input
                            type='number'
                            step='0.01'
                            className='form-control fw-bold'
                            value={previousBalanceCf === '' ? '' : previousBalanceCf}
                            onChange={(event) => handlePreviousBalanceCfChange(event.target.value)}
                            onWheel={blurNumberInputOnWheel}
                            onKeyDown={preventNumberInputArrowStep}
                            placeholder='0.00'
                          />
                          <small className='text-muted d-block mt-1' style={{fontSize: '0.75rem'}}>
                            1st month initial opening balance
                          </small>
                        </div>
                      )}
                      <div className={hasPreviousPeriodRecord ? 'col-md-4' : 'col-md-3'}>
                        <label className={LABEL}>Balance Wages</label>
                        <input
                          type='number'
                          className='form-control form-control-solid fw-bold'
                          value={Number(balanceWages || 0)}
                          readOnly
                        />
                      </div>
                      <div className={hasPreviousPeriodRecord ? 'col-md-4' : 'col-md-3'}>
                        <label className={LABEL}>Total Allotment Amount</label>
                        <input
                          type='number'
                          className='form-control form-control-solid fw-bold'
                          value={Number(totalAssignedAmount || 0)}
                          readOnly
                        />
                      </div>
                      <div className={hasPreviousPeriodRecord ? 'col-md-4' : 'col-md-3'}>
                        <label className={LABEL}>Balance C/F</label>
                        <input
                          type='number'
                          className='form-control form-control-solid fw-bold'
                          value={Number(remainingBalanceCf || 0)}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {isAddingAccount && (
                <>
                  <div className='col-12'>
                    <div className='fw-semibold text-dark'>Add New Account</div>
                    <div className='text-muted fs-8'>
                      Acc 1 stays read-only from Banking Details. Only Acc 2 and Acc 3 can be edited
                      here before submission.
                    </div>
                    {accountSaveError && (
                      <div className='text-danger fs-8 mt-1'>{accountSaveError}</div>
                    )}
                  </div>
                  <div className='col-md-3'>
                    <label className={LABEL}>
                      Beneficiary <span className='text-danger'>*</span>
                    </label>
                    <input
                      type='text'
                      className={`form-control ${
                        newAccountErrors.nameOfBeneficiary ? 'is-invalid' : ''
                      }`}
                      value={newAccountForm.nameOfBeneficiary}
                      onChange={(event) =>
                        handleNewAccountChange('nameOfBeneficiary', event.target.value)
                      }
                      placeholder='Enter beneficiary name'
                      required
                    />
                    {newAccountErrors.nameOfBeneficiary && (
                      <div className='invalid-feedback'>{newAccountErrors.nameOfBeneficiary}</div>
                    )}
                  </div>
                  <div className='col-md-3'>
                    <label className={LABEL}>
                      Bank <span className='text-danger'>*</span>
                    </label>
                    <input
                      type='text'
                      className={`form-control ${newAccountErrors.bankName ? 'is-invalid' : ''}`}
                      value={newAccountForm.bankName}
                      onChange={(event) => handleNewAccountChange('bankName', event.target.value)}
                      placeholder='Enter bank name'
                      required
                    />
                    {newAccountErrors.bankName && (
                      <div className='invalid-feedback'>{newAccountErrors.bankName}</div>
                    )}
                  </div>
                  <div className='col-md-3'>
                    <label className={LABEL}>
                      Account Number <span className='text-danger'>*</span>
                    </label>
                    <input
                      type='text'
                      className={`form-control ${
                        newAccountErrors.accountNumber ? 'is-invalid' : ''
                      }`}
                      value={newAccountForm.accountNumber}
                      onChange={(event) =>
                        handleNewAccountChange('accountNumber', event.target.value)
                      }
                      placeholder='Enter 8-20 alphanumeric account number'
                      maxLength={20}
                      required
                    />
                    {newAccountErrors.accountNumber && (
                      <div className='invalid-feedback'>{newAccountErrors.accountNumber}</div>
                    )}
                  </div>
                  <div className='col-md-3'>
                    <label className={LABEL}>
                      Branch Name <span className='text-danger'>*</span>
                    </label>
                    <input
                      type='text'
                      className={`form-control ${newAccountErrors.branchName ? 'is-invalid' : ''}`}
                      value={newAccountForm.branchName}
                      onChange={(event) => handleNewAccountChange('branchName', event.target.value)}
                      placeholder='Enter branch name'
                      required
                    />
                    {newAccountErrors.branchName && (
                      <div className='invalid-feedback'>{newAccountErrors.branchName}</div>
                    )}
                  </div>
                  <div className='col-md-3'>
                    <label className={LABEL}>
                      Branch Code <span className='text-danger'>*</span>
                    </label>
                    <input
                      type='text'
                      className={`form-control ${newAccountErrors.branchCode ? 'is-invalid' : ''}`}
                      value={newAccountForm.branchCode}
                      onChange={(event) => handleNewAccountChange('branchCode', event.target.value)}
                      placeholder='Enter 2-10 alphanumeric or hyphen'
                      maxLength={10}
                      required
                    />
                    {newAccountErrors.branchCode && (
                      <div className='invalid-feedback'>{newAccountErrors.branchCode}</div>
                    )}
                  </div>
                  <div className='col-md-3'>
                    <label className={LABEL}>
                      Bank Code <span className='text-danger'>*</span>
                    </label>
                    <SearchableSelect
                      className={`form-select ${newAccountErrors.bankCodeType ? 'is-invalid' : ''}`}
                      value={newAccountForm.bankCodeType}
                      onChange={(event) =>
                        handleNewAccountChange(
                          'bankCodeType',
                          event.target.value as BankingDetailsForm['bankCodeType']
                        )
                      }
                      required
                    >
                      <option value='IFSC_RTGS'>IFSC / RTGS</option>
                      <option value='SWIFT_IBAN'>SWIFT / IBAN</option>
                    </SearchableSelect>
                    {newAccountErrors.bankCodeType && (
                      <div className='invalid-feedback'>{newAccountErrors.bankCodeType}</div>
                    )}
                  </div>
                  <div className='col-md-3'>
                    <label className={LABEL}>
                      Bank Code Value <span className='text-danger'>*</span>
                    </label>
                    <input
                      type='text'
                      className={`form-control ${
                        newAccountErrors.bankCodeValue ? 'is-invalid' : ''
                      }`}
                      value={newAccountForm.bankCodeValue}
                      onChange={(event) =>
                        handleNewAccountChange(
                          'bankCodeValue',
                          event.target.value.replace(/\s+/g, '').toUpperCase()
                        )
                      }
                      placeholder={
                        newAccountForm.bankCodeType === 'IFSC_RTGS'
                          ? 'Enter 11-character IFSC/RTGS code'
                          : 'Enter SWIFT (8/11) or IBAN (15-34)'
                      }
                      maxLength={
                        newAccountForm.bankCodeType === 'IFSC_RTGS'
                          ? IFSC_RTGS_LENGTH
                          : IBAN_MAX_LENGTH
                      }
                      required
                    />
                    {newAccountErrors.bankCodeValue && (
                      <div className='invalid-feedback'>{newAccountErrors.bankCodeValue}</div>
                    )}
                    <small className='text-muted d-block mt-1'>
                      {newAccountForm.bankCodeType === 'IFSC_RTGS'
                        ? `IFSC/RTGS: ${IFSC_RTGS_LENGTH} characters`
                        : `SWIFT: 8 or 11 characters, IBAN: ${IBAN_MIN_LENGTH}-${IBAN_MAX_LENGTH} characters`}
                    </small>
                  </div>
                  <div className='col-md-6'>
                    <label className={LABEL}>
                      Bank Address <span className='text-danger'>*</span>
                    </label>
                    <input
                      type='text'
                      className={`form-control ${newAccountErrors.bankAddress ? 'is-invalid' : ''}`}
                      value={newAccountForm.bankAddress}
                      onChange={(event) =>
                        handleNewAccountChange('bankAddress', event.target.value)
                      }
                      placeholder='Enter bank address'
                      required
                    />
                    {newAccountErrors.bankAddress && (
                      <div className='invalid-feedback'>{newAccountErrors.bankAddress}</div>
                    )}
                  </div>
                  <div className='col-md-3'>
                    <label className={LABEL}>
                      Account Currency <span className='text-danger'>*</span>
                    </label>
                    <SearchableSelect
                      className={`form-select ${
                        newAccountErrors.accountCurrency ? 'is-invalid' : ''
                      }`}
                      value={newAccountForm.accountCurrency}
                      onChange={(event) =>
                        handleNewAccountChange(
                          'accountCurrency',
                          event.target.value as BankingDetailsForm['accountCurrency']
                        )
                      }
                      required
                    >
                      <option value='USD'>USD</option>
                      <option value='EUR'>EUR</option>
                      <option value='GBP'>GBP</option>
                      <option value='SGD'>SGD</option>
                      <option value='INR'>INR</option>
                      <option value='AED'>AED</option>
                      <option value='OTHER'>OTHER</option>
                    </SearchableSelect>
                    {newAccountErrors.accountCurrency && (
                      <div className='invalid-feedback'>{newAccountErrors.accountCurrency}</div>
                    )}
                  </div>
                  {newAccountForm.accountCurrency === 'OTHER' ? (
                    <div className='col-md-3'>
                      <label className={LABEL}>
                        Account Other Currency <span className='text-danger'>*</span>
                      </label>
                      <input
                        type='text'
                        className={`form-control ${
                          newAccountErrors.otherCurrency ? 'is-invalid' : ''
                        }`}
                        value={newAccountForm.otherCurrency}
                        onChange={(event) =>
                          handleNewAccountChange('otherCurrency', event.target.value.toUpperCase())
                        }
                        placeholder='Enter 3-letter code (e.g., INR)'
                        maxLength={3}
                        required
                      />
                      {newAccountErrors.otherCurrency && (
                        <div className='invalid-feedback'>{newAccountErrors.otherCurrency}</div>
                      )}
                    </div>
                  ) : null}
                  <div className='col-md-8'>
                    <label className={LABEL}>Remarks</label>
                    <textarea
                      className='form-control'
                      rows={2}
                      value={newAccountForm.remarks}
                      onChange={(event) => handleNewAccountChange('remarks', event.target.value)}
                      placeholder='Add remarks (optional)'
                    />
                  </div>
                  <div className='col-md-4 d-flex align-items-end'>
                    <button
                      type='button'
                      className='btn btn-sm btn-primary'
                      onClick={handleSaveNewAccount}
                      disabled={isSavingBank || !canMutateBanking}
                    >
                      {isSavingBank ? 'Saving Account...' : 'Save Account'}
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className='d-flex justify-content-end gap-3 mt-6'>
              <button type='button' className='btn btn-light' onClick={onClose} disabled={isSaving}>
                Cancel
              </button>
              <button
                type='submit'
                className='btn btn_primary'
                disabled={
                  !crewId ||
                  bankingAccounts.length === 0 ||
                  isSaving ||
                  isExceedingBalance ||
                  !canSaveAllotment
                }
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default AddAllotmentAdvanceModal
